// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { OwnProps } from "@awc/MobiusOverrides/LazyImage";
import { getFormattedDate } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandStateById } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrand from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrand";
import updateLoadParameter from "@insite/client-framework/Store/Pages/MyLists/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const getWishListsParameter = state.pages.myLists.getWishListsParameter;
    return {
        getWishListsParameter,
        selectedBrand: getBrandStateById(state, getWishListsParameter.brandId)?.value?.name || "",
    };
};

const mapDispatchToProps = {
    updateLoadParameter,
    loadBrand,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface FiltersTagStyles {
    appliedFilterTag?: TagPresentationProps;
    appliedFiltersContainer?: InjectableCss;
}

export const filtersTagStyles: FiltersTagStyles = {};

const styles = filtersTagStyles;
export const FiltersTag = ({ getWishListsParameter, selectedBrand, updateLoadParameter, loadBrand }: Props) => {
    const clearFilter = (field: string) => {
        updateLoadParameter({
            [field]: undefined,
        });
    };

    React.useEffect(() => {
        if (getWishListsParameter?.brandId) {
            loadBrand({ brandId: getWishListsParameter.brandId });
        }
    }, [getWishListsParameter?.brandId]);

    return (
        <StyledWrapper {...styles.appliedFiltersContainer}>
            {getWishListsParameter.fromCreatedDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        clearFilter("fromCreatedDate");
                    }}
                >
                    {`${translate("Created From")}: ${getFormattedDate(
                        new Date(getWishListsParameter.fromCreatedDate),
                    )}`}
                </Tag>
            )}
            {getWishListsParameter.toCreatedDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        clearFilter("toCreatedDate");
                    }}
                >
                    {`${translate("Created To")}: ${getFormattedDate(new Date(getWishListsParameter.toCreatedDate))}`}
                </Tag>
            )}
            {getWishListsParameter.fromUpdatedOn && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        clearFilter("fromUpdatedOn");
                    }}
                >
                    {`${translate("Updated From")}: ${getFormattedDate(new Date(getWishListsParameter.fromUpdatedOn))}`}
                </Tag>
            )}
            {getWishListsParameter.toUpdatedOn && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        clearFilter("toUpdatedOn");
                    }}
                >
                    {`${translate("Updated To")}: ${getFormattedDate(new Date(getWishListsParameter.toUpdatedOn))}`}
                </Tag>
            )}
            {getWishListsParameter.sharedBy && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        clearFilter("sharedBy");
                    }}
                >
                    {`${translate("Shared By")}: ${getWishListsParameter.sharedBy}`}
                </Tag>
            )}
            {getWishListsParameter.erpNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        clearFilter("erpNumber");
                        clearFilter("title");
                    }}
                >
                    {`${translate("Product")}: ${(getWishListsParameter as any).erpNumber}`}
                </Tag>
            )}
            {getWishListsParameter.brandId && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        clearFilter("brandId");
                    }}
                >
                    {`${translate("Brand")}: ${selectedBrand}`}
                </Tag>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<FieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(FiltersTag),
    definition: {
        group: "My Lists",
        displayName: "My Lists  Filters Tag",
        allowedContexts: ["MyListsPage"],
    },
};

export default widgetModule;
